import React from "react";
import { Button } from "../buttons/button";

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  itemsPerPage: number;
  pageRangeDisplayed?: number;
  onPageChange: (page: number) => void;
  className?: string;
  containerClassName?: string;
  showPagNumbers?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalCount,
  itemsPerPage,
  pageRangeDisplayed = 3,
  onPageChange,
  className = "",
  containerClassName = "",
  showPagNumbers = true,
}) => {
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const getPageNumbers = (): any => {
    if (totalPages > pageRangeDisplayed) {
      const startPage = Math.max(2, currentPage - 2);
      const endPage = Math.min(totalPages - 1, currentPage + 2);

      let pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (startPage > 2) {
        pages = ["...", ...pages];
      }
      if (endPage < totalPages - 1) {
        pages = [...pages, "..."];
      }

      return [1, ...pages, totalPages];
    }

    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const buttonClassName = `
  p-2 flex items-center justify-center gap-2
  soft-shrink rounded-md
  min-w-[30px] h-[30px]
  text-neut-800
  `;

  return (
    <nav
      className={`flex w-full items-center justify-between gap-4 overflow-y-visible overflow-x-scroll px-3 py-2 text-xs ${containerClassName}`}
    >
      <div className="text-sm font-bold text-grey-900">
        <span>
          {(currentPage - 1) * itemsPerPage + 1} -{" "}
          {currentPage * itemsPerPage > totalCount
            ? totalCount
            : currentPage * itemsPerPage}{" "}
          of {totalCount}
        </span>
      </div>

      <ul
        className={`flex flex-1 list-none flex-nowrap items-center justify-end gap-2 ${className}`}
      >
        <li
          className={` ${
            currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
          } !w-full !max-w-[115px]`}
        >
          <Button
            type="button"
            text="Previous"
            className="!rounded-2xl !py-3 !text-sm"
            variation="grey"
            onClick={() => {
              handlePageChange(currentPage - 1);
            }}
            disabled={currentPage === 1}
          />
        </li>
        {showPagNumbers &&
          getPageNumbers().map((number: number, index: number) => (
            <li key={index} className="overflow block !min-w-fit">
              {typeof number === "string" ? (
                <span className={buttonClassName}>{number}</span>
              ) : (
                <button
                  onClick={() => {
                    handlePageChange(number);
                  }}
                  className={`${buttonClassName} ${
                    number === currentPage &&
                    "bg-primary-5 border border-neutral-200 !text-primary"
                  }`}
                >
                  {number}
                </button>
              )}
            </li>
          ))}
        <li
          className={` ${
            currentPage === totalPages ? "cursor-not-allowed opacity-50" : ""
          } !w-full !max-w-[115px]`}
        >
          <Button
            type="button"
            text="Next"
            className="!rounded-2xl !py-3 !text-sm"
            variation="grey"
            onClick={() => {
              handlePageChange(currentPage + 1);
            }}
            disabled={currentPage === totalPages}
          />
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
